import { AgBarSeriesOptions, AgChartOptions } from 'ag-charts-enterprise';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { getTruckModeLabel, truckModeEnum } from '../../constants';
import { useTonnageByDriverQuery } from '../../hooks/queries/useDailyTrucksRidesQuery';
import { TonnageByDriver } from '../../services/apiSLTP/dailyReports/dailyTrucksRides';
import { formatName } from '../../utils/capitalize';
import GenericAgChart from './GenericAgChart';

interface FlattenedTonnage {
    user: string;
    [key: string]: string | number;
}

interface TonnageBarChartProps {
    startDate: string;
    endDate: string;
}

const TonnageBarChart: React.FC<TonnageBarChartProps> = ({ startDate, endDate }) => {
    const { t } = useTranslation();
    const { data: dataTonnage } = useTonnageByDriverQuery({ queries: { startDate, endDate } });

    const flattenTonnageData = (data: TonnageByDriver[]): FlattenedTonnage[] => {
        const allModes = Object.keys(truckModeEnum);

        const defaultKeys: Record<string, number> = {};

        [truckModeEnum.APPRO, truckModeEnum.POSE, truckModeEnum.DOUBLEFRET].forEach((mode) => {
            defaultKeys[`d${mode}`] = 0;
        });

        [truckModeEnum.EVAC, truckModeEnum.ENLEVEMENT, truckModeEnum.DOUBLEFRET, truckModeEnum.BACKANDFORTH, truckModeEnum.ROTATION].forEach(
            (mode) => {
                defaultKeys[`a${mode}`] = 0;
            }
        );

        return data
            .map((driver) => {
                const row: FlattenedTonnage = {
                    user: formatName(driver.user.firstName, driver.user.lastName),
                    ...defaultKeys,
                };

                driver.departureByMode.forEach(({ mode, total }) => {
                    if (total === 0) return;
                    row[`d${mode}`] = (Math.round(total * 100) / 100) * -1;
                });

                driver.arrivalByMode.forEach(({ mode, total }) => {
                    if (total === 0) return;
                    row[`a${mode}`] = Math.round(total * 100) / 100;
                });

                return row;
            })
            .sort((a, b) => a.user.localeCompare(b.user));
    };

    const generateSeries = (): AgBarSeriesOptions[] => {
        const cyanPalette = {
            [truckModeEnum.APPRO]: '#00cfd4',
            [truckModeEnum.POSE]: '#00b8c2',
            [truckModeEnum.DOUBLEFRET]: '#009fa8',
        };

        const greenPalette = {
            [truckModeEnum.EVAC]: '#90ee90',
            [truckModeEnum.ENLEVEMENT]: '#76d376',
            [truckModeEnum.DOUBLEFRET]: '#5ec85e',
            [truckModeEnum.BACKANDFORTH]: '#47bc47',
            [truckModeEnum.ROTATION]: '#30b030',
        };
        const departureSeries: AgBarSeriesOptions[] = [truckModeEnum.APPRO, truckModeEnum.POSE, truckModeEnum.DOUBLEFRET].map((key) => ({
            type: 'bar',
            yKey: `d${key}`,
            yName: `${t('source').charAt(0).toUpperCase()}. ${t(getTruckModeLabel(key))}`,
            xKey: 'user',
            direction: 'horizontal',
            stacked: true,
            fill: cyanPalette[key],

            stroke: 'white',
            strokeWidth: 2,
            cornerRadius: 4,
            strokeOpacity: 0,
            label: {
                enabled: true,
                formatter: ({ value }) => (Math.abs(value) > 0 ? `${Math.round(Math.abs(value))}` : ''),
            },
        }));

        const arrivalSeries: AgBarSeriesOptions[] = [
            truckModeEnum.EVAC,
            truckModeEnum.ENLEVEMENT,
            truckModeEnum.DOUBLEFRET,
            truckModeEnum.BACKANDFORTH,
            truckModeEnum.ROTATION,
        ].map((key) => ({
            type: 'bar',
            yKey: `a${key}`,
            yName: `${t('destination').charAt(0).toUpperCase()}. ${t(getTruckModeLabel(key))}`,
            xKey: 'user',
            direction: 'horizontal',
            stacked: true,
            fill: greenPalette[key],
            stroke: 'white',
            strokeWidth: 2,
            cornerRadius: 4,
            strokeOpacity: 0,
            label: {
                enabled: true,
                formatter: ({ value }) => (Math.abs(value) > 0 ? `${Math.round(Math.abs(value))}` : ''),
            },
        }));

        return [...departureSeries, ...arrivalSeries];
    };

    const options: AgChartOptions = {
        data: flattenTonnageData(dataTonnage || []),
        series: generateSeries(),
        minHeight: 400,
        axes: [
            {
                type: 'number',
                position: 'bottom',
                nice: false,
                label: {
                    enabled: true,
                    formatter: ({ value }) => (Math.abs(value) > 0 ? `${Math.abs(value)}` : ''),
                },
            },
            {
                type: 'category',
                position: 'left',
                line: {
                    enabled: false,
                },
            },
        ],
        padding: { top: 0, bottom: 0 },
        tooltip: {
            mode: 'shared',
            position: {
                placement: ['top', 'bottom'],
            },
        },
        legend: {
            enabled: true,
        },
    };
    return <GenericAgChart options={options} />;
};

export default TonnageBarChart;
